/**
 * Decorate internal /goon/... links with ?bd_ref=<current page URL>
 * - Works for /goon/..., /crypto/goon/..., /ai/goon/...
 * - Skips if data-bd-ref-ignore is present
 * - Handles left/middle click and Enter-key activation
 * - Resolves relative hrefs using document.baseURI (or window.location.href)
 */

import { isMobileDevice } from '../auth/helpers/deviceUtils';

const ALLOWED_DOMAINS = ['bitdegree.org', 'ogvio.com'];

const goonPathPattern = /^\/(?:[^/]+\/)?goon\//;
const baseForRelative = document.baseURI || window.location.href;

function shouldDecorate(linkElement: HTMLAnchorElement | null): linkElement is HTMLAnchorElement {
    if (!linkElement) return false;
    if (linkElement.hasAttribute('data-bd-ref-ignore')) return false;

    const rawHref = linkElement.getAttribute('href') || '';
    // Quick rejects: anchors, mailto:, tel:, javascript:, etc (only allow http/https or relative)
    if (rawHref.startsWith('#')) return false;
    if (/^[a-z]+:/i.test(rawHref) && !/^https?:/i.test(rawHref)) return false;

    try {
        const linkUrl = new URL(rawHref, baseForRelative);

        const isAllowedDomain = ALLOWED_DOMAINS.some((domain) => linkUrl.hostname === domain || linkUrl.hostname.endsWith(`.${domain}`));

        const isGoonLink = goonPathPattern.test(linkUrl.pathname);

        return isAllowedDomain && isGoonLink;
    } catch {
        return false;
    }
}

// Return the current page URL without hash; used to keep bd_ref fresh on SPA navigations
function currentRef(): string {
    const { origin, pathname, search } = window.location;

    return `${origin}${pathname}${search}`;
}

function decorate(
    linkElement: HTMLAnchorElement,
    opts: { forceUpdate?: boolean } = {},
): void {
    const hrefAttr = linkElement.getAttribute('href');
    if (!hrefAttr) return;

    const linkUrl = new URL(hrefAttr, baseForRelative);
    if (!opts.forceUpdate && linkUrl.searchParams.has('bd_ref')) {
        return;
    }

    linkUrl.searchParams.set('bd_ref', currentRef());
    linkElement.setAttribute('href', linkUrl.toString());
}

// Left / Ctrl+Click, etc.
document.addEventListener(
    'click',
    (event: MouseEvent) => {
        if (event.defaultPrevented) return;
        const linkElement = (event.target as Element | null)?.closest('a[href]') as HTMLAnchorElement | null;
        if (!shouldDecorate(linkElement)) return;
        decorate(linkElement, { forceUpdate: true });
    },
    { capture: true },
);

// Middle-clicks (auxclick = 1)
document.addEventListener(
    'auxclick',
    (event: MouseEvent) => {
        if (event.defaultPrevented || event.button !== 1) return;
        const linkElement = (event.target as Element | null)?.closest('a[href]') as HTMLAnchorElement | null;
        if (!shouldDecorate(linkElement)) return;
        decorate(linkElement, { forceUpdate: true });
    },
    { capture: true },
);

// Keyboard "Enter" on focused <a>
document.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
        if (event.defaultPrevented) return;
        if (event.key !== 'Enter') return;
        const active = document.activeElement as Element | null;
        if (!active || active.tagName !== 'A') return;
        const linkElement = active as HTMLAnchorElement;
        if (!shouldDecorate(linkElement)) return;
        decorate(linkElement, { forceUpdate: true });
    },
    { capture: true },
);

// --- Mobile-only early capture to improve bd_ref on taps / long-press open-in-new-tab ---
if (isMobileDevice()) {
    document.addEventListener(
        'pointerdown',
        (event: PointerEvent) => {
            const linkElement = (event.target as Element | null)?.closest('a[href]') as HTMLAnchorElement | null;
            if (!shouldDecorate(linkElement)) return;
            decorate(linkElement, { forceUpdate: true });
        },
        { capture: true, passive: true },
    );
}

// --- Pre-decorate existing anchors once DOM is ready (helps desktop context-menu/new-tab, routers) ---
function predecorateAll(): void {
    document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
        if (shouldDecorate(anchor)) {
            decorate(anchor);
        }
    });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', predecorateAll, { once: true });
} else {
    predecorateAll();
}

// --- Observe late-added anchors and href mutations to keep predecoration up-to-date ---
const anchorDecorationObserver = new MutationObserver((mutationList) => {
    mutationList.forEach((mutation) => {
        const isHrefAttributeChange = mutation.type === 'attributes'
            && mutation.attributeName === 'href'
            && mutation.target instanceof Element
            && mutation.target.tagName === 'A';

        if (isHrefAttributeChange) {
            const anchor = mutation.target as HTMLAnchorElement;
            if (shouldDecorate(anchor)) {
                decorate(anchor);
            }
        }

        const isNodeInsertion = mutation.type === 'childList' && mutation.addedNodes.length > 0;
        if (isNodeInsertion) {
            mutation.addedNodes.forEach((addedNode) => {
                if (!(addedNode instanceof Element)) return;

                if (addedNode.tagName === 'A' && (addedNode as HTMLAnchorElement).hasAttribute('href')) {
                    const directAnchor = addedNode as HTMLAnchorElement;
                    if (shouldDecorate(directAnchor)) {
                        decorate(directAnchor);
                    }
                }

                const descendantAnchors = addedNode.querySelectorAll?.('a[href]');
                descendantAnchors?.forEach((descendantAnchorElement) => {
                    const descendantAnchor = descendantAnchorElement as HTMLAnchorElement;
                    if (shouldDecorate(descendantAnchor)) {
                        decorate(descendantAnchor);
                    }
                });
            });
        }
    });
});

anchorDecorationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
});
